export type NumberFormatterLike = Pick<Intl.NumberFormat, "format">;
export type DateTimeFormatterLike = Pick<Intl.DateTimeFormat, "format">;
export type RelativeTimeFormatterLike = { format: (value: number, unit: Intl.RelativeTimeFormatUnit) => string };

const FALLBACK_RELATIVE_LABELS: Record<
  Intl.RelativeTimeFormatUnit,
  { past: string; future: string; zero: string }
> = {
  second: { past: "ثانیه پیش", future: "ثانیه دیگر", zero: "همین الان" },
  seconds: { past: "ثانیه پیش", future: "ثانیه دیگر", zero: "همین الان" },
  minute: { past: "دقیقه پیش", future: "دقیقه دیگر", zero: "همین الان" },
  minutes: { past: "دقیقه پیش", future: "دقیقه دیگر", zero: "همین الان" },
  hour: { past: "ساعت پیش", future: "ساعت دیگر", zero: "همین الان" },
  hours: { past: "ساعت پیش", future: "ساعت دیگر", zero: "همین الان" },
  day: { past: "روز پیش", future: "روز دیگر", zero: "امروز" },
  days: { past: "روز پیش", future: "روز دیگر", zero: "امروز" },
  week: { past: "هفته پیش", future: "هفته دیگر", zero: "همین هفته" },
  weeks: { past: "هفته پیش", future: "هفته دیگر", zero: "همین هفته" },
  month: { past: "ماه پیش", future: "ماه دیگر", zero: "همین ماه" },
  months: { past: "ماه پیش", future: "ماه دیگر", zero: "همین ماه" },
  quarter: { past: "فصل پیش", future: "فصل دیگر", zero: "همین فصل" },
  quarters: { past: "فصل پیش", future: "فصل دیگر", zero: "همین فصل" },
  year: { past: "سال پیش", future: "سال دیگر", zero: "امسال" },
  years: { past: "سال پیش", future: "سال دیگر", zero: "امسال" },
};

function sanitizeNumber(value: number): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

export function createNumberFormatter(
  locale: string,
  options?: Intl.NumberFormatOptions,
  fallbackLocale = "en-US",
): NumberFormatterLike {
  if (typeof Intl !== "undefined" && typeof Intl.NumberFormat === "function") {
    try {
      return new Intl.NumberFormat(locale, options);
    } catch {
      try {
        return new Intl.NumberFormat(locale);
      } catch {
        try {
          return new Intl.NumberFormat(fallbackLocale, options);
        } catch {
          try {
            return new Intl.NumberFormat(fallbackLocale);
          } catch {
            // ignore and fall through to manual formatter
          }
        }
      }
    }
  }

  return {
    format: (value: number) => {
      const sanitized = sanitizeNumber(value);
      if (sanitized == null) {
        return "";
      }
      const [integerPart, fractionPart] = sanitized.toString().split(".");
      const withGrouping = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return fractionPart ? `${withGrouping}.${fractionPart}` : withGrouping;
    },
  };
}

export function createDateTimeFormatter(
  locale: string,
  options?: Intl.DateTimeFormatOptions,
  fallbackLocale = "en-US",
): DateTimeFormatterLike {
  if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
    try {
      return new Intl.DateTimeFormat(locale, options);
    } catch {
      try {
        return new Intl.DateTimeFormat(locale);
      } catch {
        try {
          return new Intl.DateTimeFormat(fallbackLocale, options);
        } catch {
          try {
            return new Intl.DateTimeFormat(fallbackLocale);
          } catch {
            // ignore and fall through to manual formatter
          }
        }
      }
    }
  }

  return {
    format: (value?: number | Date) => {
      const resolved = value instanceof Date ? value : new Date(value ?? Date.now());
      if (Number.isNaN(resolved.getTime())) {
        return "";
      }
      return resolved.toISOString();
    },
  };
}

export function createRelativeTimeFormatter(
  locale: string,
  options?: Intl.RelativeTimeFormatOptions,
): RelativeTimeFormatterLike {
  const RelativeTimeFormatCtor = (Intl as typeof Intl & { RelativeTimeFormat?: typeof Intl.RelativeTimeFormat }).RelativeTimeFormat;

  if (typeof RelativeTimeFormatCtor === "function") {
    try {
      return new RelativeTimeFormatCtor(locale, options);
    } catch {
      try {
        return new RelativeTimeFormatCtor("en-US", options);
      } catch {
        // ignore and fallback to manual formatter
      }
    }
  }

  return {
    format: (value: number, unit: Intl.RelativeTimeFormatUnit) => {
      if (!value) {
        return FALLBACK_RELATIVE_LABELS[unit]?.zero ?? "همین الآن";
      }
      const dictionary = FALLBACK_RELATIVE_LABELS[unit] ?? FALLBACK_RELATIVE_LABELS.second;
      const absolute = Math.abs(Math.round(value));
      if (value < 0) {
        return `${absolute} ${dictionary.past}`;
      }
      return `${absolute} ${dictionary.future}`;
    },
  };
}
